<template>
  <div :class="getOuterStyle">
    <div class="label-font">
      <slot name="label" />

    </div>

    <div>
      <div class="icon-left">
        <slot name="icon-left" />
      </div>

      <div :class="{
        'position-relative': !unPositioned,
        'ios-correct-font-size': correctIosInputs
        }">

        <slot
          :params="getExposedParams"
          name="input" />

        <div class="footnote">
          <slot name="footnote" />
        </div>

        <div
          v-if="showErrorIcon"
          :class="{'input-error': true}">

          <span
            v-b-tooltip.hover
            :title="$t(getErrorMessage)"
            class="msg">

            <icon
              width="100%"
              name="exclamation-circle" />

          </span>
        </div>

      </div>
      <div class="icon-right">
        <slot name="icon-right" />
      </div>
    </div>
    
  </div>
</template>

<script>
import '@/assets/styles/standardInputs.css'
const EMPTY_VALUES = [null, undefined, false]

export default {
  data () {
    return {
      inputError: false,
      errorMsg: null,
      correctIosInputs: false
    }
  },

  methods: {
    /**
     * Validates input's value
     * @param {*} value
     * @param {Object} constraints
     * @returns {String|Boolean}
     * 
     * Supported constraints:
     *  * failed -- Failes input regardles of other params
     *  * required -- If input is required
     *  * equals -- If value has to equal something
     *  * optional -- If value is optional
     *  * regex -- Regex the value has to match
     *  * restrictedChars -- Chars that are not allowed to be in the value
     *  * ge -- >=
     *  * le -- <=
     *  * allowed -- if field is required and a given value is desired, that would 
     *               evalueate to false (eg. 0)
     */
    validateInput (value, constraints) {
      if (!constraints) return false

      // Can instantly fail input
      if (constraints.failed) {
        this.inputError = true
          this.errorMsg = constraints.errorMsg
          return this.inputError
      }

      // Set empty input values to '', becouse test() parses them toString()
      value = (EMPTY_VALUES.indexOf(value) > -1) ? '' : value

      // Check if required field
      if (constraints.required) {
        if (!value && value !== constraints.allowed) {
          this.inputError = true
          this.errorMsg = null
          return this.inputError
        }
      }
  
      // Check if it needs to be a speciffic value
      if (constraints.equals) {
        if (value === constraints.equals) {
          this.inputError = false
          this.errorMsg = null
          return this.inputError
        } else {
          this.inputError = true
          this.errorMsg = constraints.errorMsg
          return this.inputError
        }
      }

      // If the field is optional and the content is empty,
      // Clear the error and do nothing.
      if (constraints.optional) {
        if (!value) {
          this.inputError = false
          this.errorMsg = null
          return false
        }
      }

      // Check regex
      if (constraints.regex) {
        let { regex, flags = '' } = constraints
        regex = new RegExp(regex, flags)
        if (regex.test(value)) {
          // Input is valid
          this.inputError = false
          this.errorMsg = null
        } else {
          // Input is not valid
          this.inputError = true
          this.errorMsg = constraints.errorMsg
          return this.inputError
        }
      }

      // Check restricted chars
      if (constraints.restrictedChars) {
        let c = constraints.restrictedChars
        for (let i = 0; i < c.length; i++) {
          let char = c.charAt(i)

          if (String(value).indexOf(char) > -1) {
            // Input is not valid
            this.inputError = true
            this.errorMsg = constraints.errorMsg
            return this.inputError
          } else {
            this.inputError = false
            this.errorMsg = null
          }
        }
      }

      // Check numeric size limitations
      // Check if parsed int is a number
      if (!isNaN(parseInt(constraints.ge)) && constraints.ge !== NaN) {
        if (value >= constraints.ge) {
          this.inputError = false
          this.errorMsg = null
        } else {
          this.inputError = true
          this.errorMsg = constraints.errorMsg
          return this.inputError
        }
      }

      if (!isNaN(parseInt(constraints.le)) && constraints.le !== NaN) {
        if (value <= constraints.le) {
          this.inputError = false
          this.errorMsg = null
        } else {
          this.inputError = true
          this.errorMsg = constraints.errorMsg
          return this.inputError
        }
      }

      this.inputError = false
      return false
    }
  },

  computed: {
    isBaseInput () {
      return true
    },

    errorMessageSet () {
      return !!this.errorMsg
    },

    /**
     * Tells if error icon is visible
     * @returns {Boolean}
     */
    showErrorIcon () {
      return this.inputError && this.errorMessageSet
    },

    /**
     * Tells if error outline is visible
     * @returns {Boolean}
     */
    showErrorOutline () {
      return this.showErrorIcon
    },

    /**
     * Gives params that are expozed to parent input
     * @returns {Object}
     */
    getExposedParams () {
      return {
        showErrorOutline: this.showErrorOutline
      }
    },

    getErrorMessage () {
      return this.errorMsg || 'Check your input!'
    },

    getOuterStyle () {
      let base = ``
      switch (this.variant) {
        case 'standardInput':
          base = 'standard-outer-input-style'
          break
      }

      if (this.overlap) {
        base += ' overlap'
      }

      if (this.transparent) {
        base += ' transparent'
      }

      if (this.slimmer) {
        base += ' slimmer'
      }
      
      if (this.stretched) {
        base += ' stretched'
      }

      if (this.squished) {
        base += ' squished'
      }
      return base
    }
  },

  mounted () {
    this.correctIosInputs = !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform)
  },

  props: {
    unPositioned: {
      type: Boolean,
      required: false,
      default: false
    },
    squished: {
      type: Boolean,
      required: false,
      default: false
    },
    overlap: {
      type: Boolean,
      required: false,
      default: false
    },
    transparent: {
      type: Boolean,
      required: false,
      default: false
    },
    variant: {
      type: String,
      required: false,
      default: 'standardInput'
    },
    slimmer: {
      type: Boolean,
      required: false,
      default: false
    },
    small: {
      type: Boolean,
      required: false,
      default: false
    },
    stretched: {
      type: Boolean,
      required: false,
      default: false
    }
  }
}
</script>

<style scoped>
.footnote {
  font-size: 11px;
}
.label-font {
  position: relative;
  font-size: 10px;
  font-weight: lighter;
  color: #353635;
  bottom: 6px;
}
.label-font label {
  margin: 0;
}
.icon-right, .icon-left {
  position: absolute;
  bottom: 27px;
  display: flex;
  -ms-flex-align: center;
  align-items: center;
  justify-content: center;
  margin: 0;
}
.icon-right {
  right: 25px;
}
.icon-left {
  left: 40px;
}
.standard-outer-input-style {
  position: relative;
  background-color: #ffffff;
  padding: 12px 15px;
}

.stretched {
  padding-left: 0;
  padding-right: 0;
}

.slimmer {
  padding-top: 8px;
  padding-bottom: 8px;
}
.transparent {
  background-color: transparent;
}
.squished {
  padding-left: 0;
  padding-right: 0;
}

.input-error {
  position: absolute;
  background-color: white;
  right: -9px;
  top: -10px;
  display: flex;
}

.input-error.labeled-input {
  right: 5px;
  top: -2px;
}

.input-error .msg {
  width: 20px;
  height: 20px;
  border-radius: 100%;
  color: #FE2B41;
  display: flex;
  align-items: center;
  justify-content: center;
}

.showErrorOutline {
  border: 1px solid #FE2B41!important;
}

</style>



// WEBPACK FOOTER //
// src/components/standard/input/InputBase.vue?b9de1dea